function Pulse({ className }: { className: string }) {
  return <div className={`animate-pulse rounded bg-white/10 ${className}`} />;
}

export function PaywallSkeleton({ layout = "offers" }: { layout?: "neutral" | "offers" }) {
  if (layout === "neutral") {
    return (
      <div className="min-h-screen bg-[#0F0F14] text-white" data-paywall-skeleton-layout="neutral">
        <div className="relative mx-auto max-w-7xl px-4 py-12">
          <div className="mb-10 flex items-center gap-2">
            <Pulse className="h-5 w-5" />
            <Pulse className="h-4 w-14" />
          </div>

          <div className="mx-auto mb-10 max-w-3xl text-center md:mb-12">
            <Pulse className="mx-auto h-3 w-44" />
            <Pulse className="mx-auto mt-4 h-10 w-full max-w-lg md:h-14" />
            <Pulse className="mx-auto mt-4 h-5 w-full max-w-2xl" />
          </div>

          <div className="mx-auto max-w-6xl rounded-2xl border border-white/15 bg-[#15161B] p-6 md:p-8">
            <div className="flex flex-col justify-between gap-8 md:flex-row md:items-end">
              <div className="w-full max-w-xl">
                <Pulse className="h-3 w-32" />
                <Pulse className="mt-4 h-8 w-64 max-w-full" />
                <Pulse className="mt-3 h-4 w-full" />
                <Pulse className="mt-2 h-4 w-4/5" />
              </div>
              <div className="w-full md:w-56">
                <Pulse className="h-10 w-40" />
                <Pulse className="mt-4 h-12 w-full" />
              </div>
            </div>

            <div className="mt-8 grid grid-cols-2 gap-4 border-t border-white/10 pt-6 md:grid-cols-4">
              {[1, 2, 3, 4].map((feature) => (
                <div className="flex items-center gap-2" key={feature}>
                  <Pulse className="h-4 w-4 shrink-0" />
                  <Pulse className="h-4 w-3/4" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0F0F14] text-white" data-paywall-skeleton-layout="offers">
      <div className="relative mx-auto max-w-7xl px-4 py-12">
        <div className="mb-10 flex items-center gap-2">
          <Pulse className="h-5 w-5" />
          <Pulse className="h-4 w-14" />
        </div>

        <div className="mx-auto mb-10 max-w-3xl text-center md:mb-12">
          <Pulse className="mx-auto h-3 w-48" />
          <Pulse className="mx-auto mt-4 h-10 w-full max-w-xl md:h-14" />
          <Pulse className="mx-auto mt-4 h-5 w-full max-w-2xl" />
        </div>

        <div className="mx-auto mb-10 grid max-w-6xl grid-cols-1 gap-5 md:mb-12 md:grid-cols-2">
          <div className="flex min-h-[590px] flex-col rounded-2xl border border-white/15 bg-[#15161B] p-6 md:p-8">
            <Pulse className="h-3 w-32" />
            <Pulse className="mt-4 h-8 w-64 max-w-full" />
            <Pulse className="mt-3 h-4 w-4/5" />

            <div className="mt-6 border-t border-white/10 pt-5">
              <Pulse className="h-5 w-36" />
              <Pulse className="mt-2 h-11 w-full" />
            </div>

            <div className="mt-5">
              <Pulse className="h-10 w-48" />
              <Pulse className="mt-3 h-3 w-56 max-w-full" />
            </div>

            <div className="mt-6 grid grid-cols-2 gap-4 border-t border-white/10 pt-5">
              {[1, 2, 3, 4].map((feature) => (
                <div className="flex items-center gap-2" key={feature}>
                  <Pulse className="h-4 w-4 shrink-0" />
                  <Pulse className="h-4 w-3/4" />
                </div>
              ))}
            </div>

            <Pulse className="mt-auto h-12 w-full" />
          </div>

          <div className="flex min-h-[590px] flex-col rounded-2xl border border-white/15 bg-[#15161B] p-6 md:p-8">
            <Pulse className="h-3 w-32" />
            <Pulse className="mt-4 h-8 w-64 max-w-full" />
            <Pulse className="mt-3 h-4 w-full" />
            <Pulse className="mt-2 h-4 w-4/5" />

            <div className="mt-6">
              <Pulse className="h-10 w-48" />
              <Pulse className="mt-3 h-3 w-72 max-w-full" />
              <Pulse className="mt-3 h-4 w-64 max-w-full" />
            </div>

            <div className="mt-6 border-t border-white/10 pt-5">
              <Pulse className="h-4 w-28" />
              <Pulse className="mt-2 h-11 w-full" />
            </div>

            <div className="mt-6 border-t border-white/10 pt-5">
              <Pulse className="h-3 w-32" />
              <div className="mt-3 grid grid-cols-2 gap-px overflow-hidden rounded-lg border border-white/10">
                {[1, 2, 3, 4].map((fact) => (
                  <Pulse className="h-16 w-full rounded-none" key={fact} />
                ))}
              </div>
            </div>

            <Pulse className="mt-auto h-12 w-full" />
          </div>
        </div>

        <div className="mb-10 text-center">
          <Pulse className="mx-auto h-4 w-36" />
        </div>
      </div>

      <div className="py-10 md:py-14">
        <div className="mx-auto mb-8 max-w-6xl px-4 text-center">
          <Pulse className="mx-auto h-3 w-28" />
          <Pulse className="mx-auto mt-3 h-7 w-80 max-w-full" />
        </div>
        <div className="mx-auto grid max-w-6xl grid-cols-1 gap-5 px-4 md:grid-cols-3">
          {[1, 2, 3].map((testimonial) => (
            <div
              className="flex min-h-64 flex-col rounded-2xl border border-white/10 bg-[#15161B] p-6"
              key={testimonial}
            >
              <Pulse className="h-4 w-24" />
              <Pulse className="mt-5 h-4 w-full" />
              <Pulse className="mt-3 h-4 w-full" />
              <Pulse className="mt-3 h-4 w-4/5" />
              <Pulse className="mt-auto h-4 w-32" />
              <Pulse className="mt-2 h-3 w-40" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
